import { queryOptions } from '@tanstack/react-query';
import { getUserSettings } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import { queryKeys } from './query-keys';

/** Player account details from `sb/api/player/profile/details`. */
export interface MachineProfile {
  MachineGuid?: string;
  Currency?: string;
  CurrencyId?: number | null;
  Username?: string;
  FirstName?: string;
  LastName?: string;
  BirthDate?: string | null;
  Email?: string;
  Telephone?: string;
  Address?: string;
  Zip?: string;
  City?: string;
  Country?: string;
  OldPassword?: string;
  NewPassword?: string;
  ConfirmPassword?: string;
  CitizenId?: string;
}

/** Shared UsersAPI form response (`FormStatus`). */
export interface FormStatus {
  Success?: boolean;
  Error?: string | null;
  Result?: unknown;
  Id?: string | null;
  MachineGuid?: string | null;
  Credits?: number | null;
  CreditsData?: unknown;
}

export interface EditPlayerDetailsRequest {
  MachineGuid: string;
  Username?: string;
  Email?: string;
  OldPassword?: string;
  NewPassword?: string;
  ConfirmPassword?: string;
  FirstName?: string;
  LastName?: string;
  Address?: string;
  Telephone?: string;
}

/** Loads identity fields for the logged-in player. */
export async function fetchPlayerProfile(
  machineGuid: string,
  signal?: AbortSignal,
): Promise<MachineProfile> {
  return api.post<MachineProfile>(
    endpoints.player.details.host,
    endpoints.player.details.path,
    { Value: machineGuid },
    signal,
  );
}

/** Updates login details / password via `sb/api/player/edit/details`. */
export async function editPlayerDetails(
  payload: EditPlayerDetailsRequest,
  signal?: AbortSignal,
): Promise<FormStatus> {
  return api.post<FormStatus>(
    endpoints.player.editDetails.host,
    endpoints.player.editDetails.path,
    payload as unknown as Record<string, unknown>,
    signal,
  );
}

/** React Query options for the current player's profile details. */
export function playerProfileQuery(machineGuid: string | null | undefined) {
  const guid = machineGuid?.trim() || '';
  return queryOptions({
    queryKey: [...queryKeys.player.profile(), guid] as const,
    queryFn: ({ signal }) => fetchPlayerProfile(guid, signal),
    enabled: guid.length > 0,
    staleTime: 60_000,
  });
}

/** MachineGuid from the session, or null when logged out. */
export function currentMachineGuid(): string | null {
  const guid = getUserSettings()?.Guid?.trim();
  return guid || null;
}

export interface BirthDateParts {
  day: string;
  month: string;
  year: string;
}

/**
 * Splits an API birth date into padded DD / MM / YYYY for the details form.
 * Accepts ISO `YYYY-MM-DD`, `DD/MM/YYYY`, and Date-parseable strings.
 */
export function parseBirthDateParts(value: string | null | undefined): BirthDateParts {
  const empty = { day: '', month: '', year: '' };
  if (!value || typeof value !== 'string') return empty;
  const trimmed = value.trim();
  if (!trimmed) return empty;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(trimmed);
  if (iso) {
    return {
      year: iso[1]!,
      month: iso[2]!.padStart(2, '0'),
      day: iso[3]!.padStart(2, '0'),
    };
  }

  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/.exec(trimmed);
  if (dmy) {
    return {
      day: dmy[1]!.padStart(2, '0'),
      month: dmy[2]!.padStart(2, '0'),
      year: dmy[3]!,
    };
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return empty;
  return {
    day: String(parsed.getUTCDate()).padStart(2, '0'),
    month: String(parsed.getUTCMonth() + 1).padStart(2, '0'),
    year: String(parsed.getUTCFullYear()),
  };
}

/** Initials from first/last name, falling back to username. */
export function profileInitials(
  profile: Pick<MachineProfile, 'FirstName' | 'LastName' | 'Username'> | null | undefined,
): string {
  const first = profile?.FirstName?.trim().charAt(0) ?? '';
  const last = profile?.LastName?.trim().charAt(0) ?? '';
  if (first || last) return `${first}${last}`.toUpperCase();
  const user = profile?.Username?.trim().charAt(0) ?? '';
  return user ? user.toUpperCase() : '?';
}

/** Display name: "First Last", else username. */
export function profileDisplayName(
  profile: Pick<MachineProfile, 'FirstName' | 'LastName' | 'Username'> | null | undefined,
): string {
  const name = [profile?.FirstName?.trim(), profile?.LastName?.trim()].filter(Boolean).join(' ');
  if (name) return name;
  return profile?.Username?.trim() ?? '';
}
